import React from "react"
import { useStripe, useElements } from "@stripe/react-stripe-js"
import AldooStoreAPI from "aldoo-ra/Store/api"

// Wrapper component that provides stripe/elements context to invoice flow
const CheckoutWithInvoice = ({ children, store, invoiceData, onComplete }) => {
  const stripe = useStripe()
  const elements = useElements()

  // Add completePaymentWithInvoice function to store
  React.useEffect(() => {
    store.completePaymentWithInvoice = async (data) => {
      try {
        const { paymentInfo, onPaymentSuccess, onPaymentError } = store

        // Update customer invoice details if needed
        if (data.needsInvoice) {
          const addressElement = elements.getElement('address')
          if (addressElement) {
            const { complete, value: addressValue } = await addressElement.getValue()

            if (complete && addressValue) {
              await AldooStoreAPI.updateCustomerInvoiceDetails({
                companyName: data.companyName,
                taxId: data.taxId,
                address: {
                  line1: addressValue.address.line1,
                  line2: addressValue.address.line2,
                  city: addressValue.address.city,
                  state: addressValue.address.state,
                  postal_code: addressValue.address.postal_code,
                  country: addressValue.address.country,
                },
                email: addressValue.email || undefined,
                name: addressValue.name || undefined,
              })
            }
          }
        }

        // Now complete the payment
        const return_url = `${window.location.origin}/payment-result`
        const isSetupIntent = paymentInfo.clientSecret?.startsWith("seti_")
        const isPaymentIntent = paymentInfo.clientSecret?.startsWith("pi_")

        if (isSetupIntent || paymentInfo.type === "trial_setup") {
          const setupResult = await stripe.confirmSetup({
            elements,
            redirect: "if_required",
            confirmParams: {
              return_url,
            },
          })

          if (setupResult.error) {
            onPaymentError(setupResult.error)
            return
          }

          const retrieveResult = await stripe.retrieveSetupIntent(paymentInfo.clientSecret)
          const { setupIntent } = retrieveResult

          if (setupIntent.status === "succeeded") {
            await AldooStoreAPI.completeSubscriptionAfterSetup({
              setupIntentId: setupIntent.id,
              pid: paymentInfo.pid,
              promo_code: paymentInfo.promoCode,
            })
          }
        } else if (isPaymentIntent) {
          const result = await stripe.confirmPayment({
            elements,
            redirect: "if_required",
            confirmParams: {
              return_url,
            },
          })

          if (result.error) {
            onPaymentError(result.error)
            return
          }

          // Capture payment intent ID from the confirmation result
          if (result.paymentIntent) {
            onPaymentSuccess({ paymentIntentId: result.paymentIntent.id })
            return
          }
        }

        onPaymentSuccess()
      } catch (error) {
        store.onPaymentError(error)
      }
    }

    return () => {
      delete store.completePaymentWithInvoice
    }
  }, [stripe, elements, store, invoiceData])

  return <>{children}</>
}

export default CheckoutWithInvoice
